import _ from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { Stack, useTheme } from '@mui/system';
import { Typography } from '@mui/material';
// sections
// reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import {
	getBeneficiaryDetails,
	getBeneficiaryBankDetails,
	getBeneficiaryPayments,
	getBeneficiaryPaymentsStatus,
	getNewBeneficiary,
	getBeneficiaryPaymentsProfilePage,
	getBeneficiaryDetailsCompanyForDisplay,
	getPaymentCategoryOptions,
} from 'state/reducers/beneficiary.reducers';
import { getCountries, getCurrencies } from 'state/reducers/global.reducers';
//components
import { SimpleList, ListHeader2, ListHeaderWithActions, ListWrap } from 'theme/elements/List';
import { Paragraph1, Paragraph2, Paragraph3 } from 'theme/elements/Paragraphs';
//constants
import { pagesEnum } from 'constants/ui.constants';
import { Beneficiary, BeneficiaryStatus, PaymentCategoryOption } from 'constants/beneficiary.constants';
import { DetailsColumnCard2 } from 'theme/elements/Cards';
import { CustomChip, ChipType } from 'theme/elements/Chips';
import CancelDialog from 'components/CancelDialog';
import { Fragment, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteBeneficiary } from 'services/beneficiary.services';
import { getUserDetails, getUserPermission } from 'state/reducers/user.reducers';
import { UserPermission } from 'constants/user.constants';
import { Download } from '@mui/icons-material';
import { getFile } from 'services/global.services';
import PaymentCategoryDisplay from 'components/PaymentCategoryDisplay';
import { getLabel, prefixBankDetails } from 'sections/beneficiary/upsert/helper';
import { PaymentStatus } from 'constants/pay.constants';
import WarningDialog from 'components/WarningDialog';
import { buildCategoryGroups } from 'services/beneficiary.services';
import { createCategoryMap } from 'utils/paymentCategoryUtils';

export default function Details() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];
	const details = useAppSelector(getBeneficiaryDetailsCompanyForDisplay);
	const bank = useAppSelector(getBeneficiaryBankDetails);
	const theme = useTheme();
	const beneficiary = useAppSelector(getNewBeneficiary);
	const countries = _.keyBy(useAppSelector(getCountries), 'id');
	const currencies = _.keyBy(useAppSelector(getCurrencies), 'id');
	const payments = useAppSelector(getBeneficiaryPaymentsProfilePage);
	const t = useAppSelector(selectTranslations);
	const imageUrl = useAppSelector(selectImageUrl);
	const paymentStatus = useAppSelector(getBeneficiaryPaymentsStatus);
	const prefixFields = `page.beneficiary.profile.`;
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const [cantEditDialog, setCantEditDialog] = useState(false);
	const userPermission = useAppSelector(getUserPermission);
	const user = useAppSelector(getUserDetails);
	const prefixFields1 = `page.beneficiary.create.step.1.beneficiary_details.`;
	const categoryOptions = useAppSelector(getPaymentCategoryOptions);

	const formatValues = (list: Partial<Beneficiary>) => {
		let obj = {};
		_.keys(list).forEach((key) => {
			switch (key) {
				case 'countryId':
				case 'bankAccountCountryId':
					obj[key] = _.get(countries, list[key] || '')?.name;
					break;
				case 'currencyId':
					obj[key] = _.get(currencies, list[key] || '')?.name;
					break;
				case 'files':
					if (_.isArray(beneficiary.files)) {
						obj[key] = beneficiary.files;
					} else {
						obj[key] = list[key];
					}
					break;
				case 'formalBankApproveFile':
					obj[key] = list[key]
						? decodeURI(_.get(beneficiary, 'formalBankApproveFile.originalFileName'))
						: list[key];
					break;
				default:
					obj[key] = list[key];
			}
		});
		return obj;
	};

	const getAction = (index) => {
		switch (_.keys(details)[index]) {
			case 'files':
			case 'formalBankApproveFile':
				return (
					<Download
						cursor={'pointer'}
						onClick={() =>
							dispatch(getFile(_.get(beneficiary, `${_.keys(details)[index]}.key`), user.companyId))
						}
					/>
				);
			default:
				return '';
		}
	};

	const renderFilesList = (files: any[]) => {
		if (!files || !_.isArray(files) || files.length === 0) {
			return null;
		}

		return (
			<Stack gap={1}>
				{files.map((file, fileIndex) => (
					<Stack key={fileIndex} flexDirection={'row'} alignItems="center" gap={1}>
						<Paragraph3 title="" text={file.originalFileName ? decodeURI(file.originalFileName) : 'File'} />
						<Download
							cursor={'pointer'}
							onClick={() => dispatch(getFile(_.get(file, 'key'), user.companyId))}
						/>
					</Stack>
				))}
			</Stack>
		);
	};

	const hasPaymentsInProgress = useMemo(() => {
		return _.find(
			beneficiary?.paymentsList || [],
			(p) => ![PaymentStatus.Completed, PaymentStatus.Failed, PaymentStatus.Rejected].includes(p.status)
		);
	}, [beneficiary]);

	const categoryMap = useMemo(() => createCategoryMap(categoryOptions), [categoryOptions]);

	const groupedCategories = useMemo(() => {
		// Use the already-flattened paymentCategories with proper primary/secondary/tertiary structure
		return buildCategoryGroups(beneficiary?.paymentCategories || [], categoryMap);
	}, [beneficiary?.paymentCategories, categoryMap]);

	return (
		<Stack gap={2.5}>
			<DetailsColumnCard2>
				<ListWrap>
					<ListHeaderWithActions title={t(`${prefixFields}beneficiary_details.title`)}>
						<>
							{_.includes([UserPermission.OPERATIONER, UserPermission.SIGNER], userPermission) &&
								_.includes(
									[
										BeneficiaryStatus.Approved,
										BeneficiaryStatus.ApprovedWithRisk,
										BeneficiaryStatus.PendingBankAccountDetails,
										BeneficiaryStatus.DeclinedForRepair,
									],
									beneficiary.status
								) && (
									<>
										<img
											src={imageUrl(`${prefixFields}edit.icon`) || '/icons/edit.svg'}
											width="40"
											height="40"
											onClick={() => {
												if (hasPaymentsInProgress) {
													setCantEditDialog(true);
												} else {
													navigate(`/${pagesEnum.BENEFICIARY}/edit/${lastPath}`);
												}
											}}
										/>
										{_.includes([UserPermission.SIGNER], userPermission) && (
											<img
												src={imageUrl(`${prefixFields}trash.icon`) || '/icons/trash.svg'}
												width="40"
												height="40"
												onClick={() => {
													if (hasPaymentsInProgress) {
														setCantEditDialog(true);
													} else {
														setOpenCancelDialog(!openCancelDialog);
													}
												}}
											/>
										)}
									</>
								)}
						</>
					</ListHeaderWithActions>
					<Stack flexDirection={'row'}>
						<Stack gap={2} width={'50%'}>
							{_.values(formatValues(details)).map((field, index) => {
								return index < 4 ? (
									<Paragraph3 key={index} title={getLabel(details, index, t)} text={String(field)} />
								) : (
									<Fragment key={index}></Fragment>
								);
							})}
						</Stack>
						<Stack gap={2}>
							{_.values(formatValues(details)).map((field, index) => {
								const fieldKey = _.keys(details)[index];

								if (index <= 3 || !field) {
									return <Fragment key={index}></Fragment>;
								}

								if (fieldKey === 'files' && _.isArray(field)) {
									return (
										<Stack key={index} gap={1}>
											<Paragraph3 title={getLabel(details, index, t)} text="" />
											{renderFilesList(field)}
										</Stack>
									);
								}

								return (
									<Stack key={index} flexDirection={'row'}>
										<Paragraph3 title={getLabel(details, index, t)} text={String(field)} />
										{getAction(index)}
									</Stack>
								);
							})}
							{beneficiary.website && (
								<Paragraph3
									title={t(`${prefixFields1}website.text`)}
									text={_.get(beneficiary, 'website')}
								/>
							)}
						</Stack>
					</Stack>
				</ListWrap>
			</DetailsColumnCard2>
			<DetailsColumnCard2>
				<Stack spacing={3}>
					<ListWrap>
						<ListHeader2 title={t(`${prefixFields}bank_account_details.title`)} />
						<Stack flexDirection={'row'}>
							<Stack gap={2} width={'50%'}>
								{_.values(formatValues(bank)).map((field, index) =>
									field ? (
										<Paragraph3
											key={index}
											title={t(
												`${prefixBankDetails}${_.snakeCase(Object.keys(bank)[index])}.text`
											)}
											text={String(field)}
										/>
									) : (
										<></>
									)
								)}
							</Stack>
							<Stack gap={2}>
								{_.keys(payments).map((field, index) => {
									return (
										<Paragraph3
											key={index}
											title={t(`${prefixFields}payment_method.${field}.text`)}
											text={_.get(payments, field)}
										/>
									);
								})}
							</Stack>
						</Stack>
					</ListWrap>
				</Stack>
			</DetailsColumnCard2>
			{beneficiary?.paymentCategories && beneficiary.paymentCategories.length > 0 && (
				<DetailsColumnCard2>
					<ListWrap>
						<ListHeader2 title={t('page.beneficiary.details.payment_categories.title')} />
						<PaymentCategoryDisplay
							primOrder={groupedCategories.primOrder}
							groups={groupedCategories.groups}
						/>
					</ListWrap>
				</DetailsColumnCard2>
			)}
			<DetailsColumnCard2>
				<ListWrap>
					<ListHeader2 title={t(prefixFields + 'payment_type.title')} />
					<Stack direction="row" spacing={1}>
						<CustomChip
							type={paymentStatus.isPriorityPaymentAllowed ? ChipType.REGULAR : ChipType.GRAYED}
							icon={paymentStatus.isPriorityPaymentAllowed ? 'done' : 'close'}
							label={t(`${prefixFields}payment_type.priority.text`)}
							styleObj={{
								'& .MuiSvgIcon-root': {
									color: paymentStatus.isPriorityPaymentAllowed
										? theme.palette.root.brandSecondary
										: 'inherit',
								},
							}}
						/>
						<CustomChip
							type={paymentStatus.isLocalPaymentAllowed ? ChipType.REGULAR : ChipType.GRAYED}
							icon={paymentStatus.isLocalPaymentAllowed ? 'done' : 'close'}
							label={t(`${prefixFields}payment_type.regular.text`)}
							styleObj={{
								'& .MuiSvgIcon-root': {
									color: paymentStatus.isLocalPaymentAllowed
										? theme.palette.root.brandSecondary
										: 'inherit',
								},
							}}
						/>
					</Stack>
				</ListWrap>
			</DetailsColumnCard2>
			<CancelDialog
				open={openCancelDialog}
				handleContinue={() => setOpenCancelDialog(false)}
				handleCancel={() => {
					setOpenCancelDialog(false);
					dispatch(deleteBeneficiary(beneficiary.id));
				}}
				cancelLabel={t(prefixFields + 'delete_popup.approve_button.text')}
				continueLabel={t(prefixFields + 'delete_popup.cancel_button.text')}
				title={t(prefixFields + 'delete_popup.header.title')}
				subTitle={t(prefixFields + 'delete_popup.header.text')}
			/>
			<WarningDialog
				open={cantEditDialog}
				title={t(prefixFields + 'header.cannot_edit.text')}
				handleCancel={() => setCantEditDialog(false)}
			/>
		</Stack>
	);
}
